import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CalendarDays, TrendingUp, TrendingDown, Clock, AlertCircle, Loader2, RefreshCw, Wifi, Mail, Upload, Save, History, Trash2, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PortfolioFileImport, ImportedPosition } from '@/components/PortfolioFileImport';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { format } from 'date-fns';
import EarningsSurpriseChart from '@/components/EarningsSurpriseChart';

interface EarningsEvent {
  ticker: string;
  stockName?: string;
  earningsDate: string;
  daysUntil: number;
  timing: 'BMO' | 'AMC' | 'Unknown';
  epsEstimate?: number;
  epsActual?: number;
  revenueEstimate?: number;
  revenueActual?: number;
  surprise?: number;
  status: 'upcoming' | 'reported';
  numAnalysts?: number;
}

interface SavedSnapshot {
  id: string;
  created_at: string;
  positions_count: number;
  total_value: number;
  positions_data: any[];
  analysis_data: any;
}

const EarningsCalendar = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [positions, setPositions] = useState<any[]>([]);
  const [earningsData, setEarningsData] = useState<EarningsEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'reported'>('all');
  const [timeRange, setTimeRange] = useState<'30' | '60' | '90'>('60');
  const [dataSource, setDataSource] = useState<'live' | 'import' | 'none'>('none');
  const [apiSource, setApiSource] = useState<string | null>(null);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(() => localStorage.getItem('earnings_alert_recipient') || '');
  const [showRecipientInput, setShowRecipientInput] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importedPositions, setImportedPositions] = useState<ImportedPosition[]>([]);
  const [savedSnapshots, setSavedSnapshots] = useState<SavedSnapshot[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadPositions();
      loadSnapshots();
    }
  }, [user]);

  const loadPositions = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('positions')
      .select('ticker, stock_name')
      .eq('user_id', user.id);
    if (data && data.length > 0) {
      setPositions(data);
      fetchLiveEarnings(data);
    }
  };

  const loadSnapshots = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('portfolio_snapshots')
      .select('*')
      .eq('user_id', user.id)
      .eq('snapshot_type', 'earnings')
      .order('created_at', { ascending: false })
      .limit(10);
    setSavedSnapshots((data as any[]) || []);
  }, [user]);

  const fetchLiveEarnings = async (posData: any[]) => {
    setLoading(true);
    try {
      const now = new Date();
      const days = parseInt(timeRange);
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      const to = new Date(now);
      to.setDate(to.getDate() + days);

      const tickers = posData.map(p => {
        const t = p.ticker || p.symbol || '';
        return t.replace(/\s.*$/, '').toUpperCase();
      });
      const stockNameMap = new Map(posData.map(p => {
        const t = (p.ticker || p.symbol || '').replace(/\s.*$/, '').toUpperCase();
        return [t, p.stock_name || p.stockName || p.ticker || t];
      }));

      const { data, error } = await supabase.functions.invoke('earnings-calendar', {
        body: {
          tickers,
          from: from.toISOString().split('T')[0],
          to: to.toISOString().split('T')[0],
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to fetch earnings');

      const events: EarningsEvent[] = (data.results || []).map((item: any) => {
        const earningsDate = item.date;
        const daysUntil = Math.ceil((new Date(earningsDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const isPast = daysUntil < 0;
        const hasActual = item.eps != null && item.eps !== 0;

        let timing: 'BMO' | 'AMC' | 'Unknown' = 'Unknown';
        const t = (item.time || '').toLowerCase();
        if (t.includes('bmo') || t.includes('before')) timing = 'BMO';
        else if (t.includes('amc') || t.includes('after')) timing = 'AMC';

        const surprise = hasActual && item.epsEstimated
          ? parseFloat((((item.eps - item.epsEstimated) / Math.abs(item.epsEstimated)) * 100).toFixed(1))
          : undefined;

        return {
          ticker: item.symbol,
          stockName: stockNameMap.get(item.symbol.toUpperCase()) || item.symbol,
          earningsDate,
          daysUntil,
          timing,
          epsEstimate: item.epsEstimated,
          epsActual: hasActual ? item.eps : undefined,
          revenueEstimate: item.revenueEstimated ? item.revenueEstimated / 1e9 : undefined,
          revenueActual: (item.revenue && item.revenue !== 0) ? item.revenue / 1e9 : undefined,
          surprise,
          status: (isPast || hasActual) ? 'reported' : 'upcoming',
          numAnalysts: item.numberAnalystsEstimatedEps,
        };
      });

      events.sort((a, b) => new Date(a.earningsDate).getTime() - new Date(b.earningsDate).getTime());
      setEarningsData(events);
      setDataSource('live');
      setApiSource(data.source || 'fmp');
      const source = data.source === 'nasdaq' ? 'Nasdaq' : 'Financial Modeling Prep';
      toast({ title: "Live Data Loaded", description: `${events.length} earnings events from ${source}` });
    } catch (err: any) {
      console.error('Earnings fetch error:', err);
      toast({ title: "API Error", description: err.message || 'Failed to fetch earnings data', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (dataSource === 'import' && importedPositions.length > 0) {
      fetchLiveEarnings(importedPositions.map(p => ({ ticker: p.ticker, stock_name: p.stockName })));
    } else if (positions.length > 0) {
      fetchLiveEarnings(positions);
    }
  };

  const handleImport = (positionsData: ImportedPosition[]) => {
    setImportedPositions(positionsData);
    setDataSource('import');
    setShowImport(false);
    // Fetch earnings for imported tickers
    const posData = positionsData.map(p => ({ ticker: p.ticker, stock_name: p.stockName }));
    fetchLiveEarnings(posData);
    toast({ title: "Import Complete", description: `${positionsData.length} positions imported — fetching earnings...` });
  };

  const saveSnapshot = async () => {
    if (!user || earningsData.length === 0) return;
    setSaving(true);
    try {
      const posData = dataSource === 'import' ? importedPositions : positions;
      const { error } = await supabase.from('portfolio_snapshots').insert({
        user_id: user.id,
        snapshot_type: 'earnings',
        positions_data: posData as any,
        analysis_data: {
          earningsData,
          source: dataSource,
          timeRange,
          fetchedAt: new Date().toISOString(),
        } as any,
        metadata: { source: dataSource, importedAt: new Date().toISOString() } as any,
        positions_count: posData.length,
        total_value: earningsData.length,
      });
      if (error) throw error;
      toast({ title: "Saved", description: "Earnings analysis saved." });
      loadSnapshots();
    } catch (e: any) {
      toast({ title: "Save Failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const restoreSnapshot = (snap: SavedSnapshot) => {
    const analysis = snap.analysis_data as any;
    if (analysis?.earningsData) {
      setEarningsData(analysis.earningsData);
      if (analysis.source === 'import') {
        setImportedPositions(snap.positions_data as ImportedPosition[]);
        setDataSource('import');
      } else {
        setDataSource('live');
      }
      if (analysis.timeRange) setTimeRange(analysis.timeRange);
      toast({ title: "Restored", description: `Loaded snapshot from ${format(new Date(snap.created_at), "dd MMM yyyy HH:mm")}` });
    }
  };

  const deleteSnapshot = async (id: string) => {
    await supabase.from('portfolio_snapshots').delete().eq('id', id);
    setSavedSnapshots(prev => prev.filter(s => s.id !== id));
    toast({ title: "Deleted" });
  };

  const sendEarningsAlert = async () => {
    if (!user) return;
    setSendingAlert(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-earnings-alerts', {
        body: { userId: user.id, recipientEmail: recipientEmail.trim() || undefined },
      });
      if (error) throw error;
      if (data?.success) {
        toast({
          title: '📧 Earnings Alert Sent',
          description: `Email sent with ${data.earningsCount} upcoming earnings (${data.tickers.join(', ')})`,
        });
      } else {
        toast({
          title: 'No upcoming earnings',
          description: data?.message || 'No portfolio holdings reporting within 3 days.',
        });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to send alert', variant: 'destructive' });
    } finally {
      setSendingAlert(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return earningsData;
    return earningsData.filter(e => e.status === filter);
  }, [earningsData, filter]);

  const upcomingCount = earningsData.filter(e => e.status === 'upcoming').length;
  const reportedCount = earningsData.filter(e => e.status === 'reported').length;
  const beatsCount = earningsData.filter(e => e.surprise != null && e.surprise > 0).length;
  const missesCount = earningsData.filter(e => e.surprise != null && e.surprise < 0).length;
  const nextEarnings = earningsData.find(e => e.status === 'upcoming');
  const hasData = earningsData.length > 0;

  const getTimingBadge = (timing: string) => {
    switch (timing) {
      case 'BMO': return <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">Pre-Market</Badge>;
      case 'AMC': return <Badge variant="outline" className="bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/30">After Hours</Badge>;
      default: return <Badge variant="outline" className="bg-muted text-muted-foreground">TBD</Badge>;
    }
  };

  const getSurpriseBadge = (surprise?: number) => {
    if (surprise === undefined) return <span className="text-muted-foreground">—</span>;
    if (surprise > 0) return <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30">Beat +{surprise}%</Badge>;
    if (surprise < 0) return <Badge className="bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30">Miss {surprise}%</Badge>;
    return <Badge variant="outline">In-line</Badge>;
  };

  const getDaysLabel = (days: number) => {
    if (days === 0) return <span className="font-bold text-amber-600 dark:text-amber-400">Today</span>;
    if (days === 1) return <span className="font-bold text-amber-600 dark:text-amber-400">Tomorrow</span>;
    if (days < 0) return <span className="text-muted-foreground">{Math.abs(days)}d ago</span>;
    if (days <= 7) return <span className="font-semibold text-orange-600 dark:text-orange-400">{days}d</span>;
    return <span className="text-muted-foreground">{days}d</span>;
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Please sign in to view earnings calendar.</p>
          <Button className="mt-4" onClick={() => navigate('/')}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Button>
            {dataSource === 'live' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className={`gap-1 cursor-help ${apiSource === 'nasdaq' ? 'text-blue-600 dark:text-blue-400 border-blue-500/30 bg-blue-500/10' : 'text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10'}`}>
                      <Wifi className="h-3 w-3" /> Live — {apiSource === 'nasdaq' ? 'Nasdaq' : 'FMP'}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                    {apiSource === 'nasdaq'
                      ? 'Using Nasdaq API as fallback — FMP returned a rate-limit or payment error.'
                      : 'Using Financial Modeling Prep as the primary data source. Falls back to Nasdaq if unavailable.'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {dataSource === 'import' && (
              <Badge variant="outline" className="gap-1 text-blue-600 dark:text-blue-400 border-blue-500/30 bg-blue-500/10">
                <Upload className="h-3 w-3" /> Imported — {importedPositions.length} pos
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasData && (
              <Button variant="outline" size="sm" onClick={saveSnapshot} disabled={saving} className="gap-2">
                <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} className="gap-2">
              <Upload className="h-4 w-4" /> Import
            </Button>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setShowRecipientInput(!showRecipientInput)} className="gap-1 px-2">
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={sendEarningsAlert} disabled={sendingAlert || (positions.length === 0 && importedPositions.length === 0)} className="gap-2">
                {sendingAlert ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {sendingAlert ? 'Sending...' : recipientEmail.trim() ? `Alert ${recipientEmail.trim().split('@')[0]}` : 'Email Alert'}
              </Button>
            </div>
            {showRecipientInput && (
              <Input
                type="email"
                placeholder="Recipient email (leave empty for default)"
                value={recipientEmail}
                onChange={(e) => {
                  setRecipientEmail(e.target.value);
                  localStorage.setItem('earnings_alert_recipient', e.target.value);
                }}
                className="w-56 h-8 text-xs"
              />
            )}
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <ThemeToggle />
          </div>
        </div>

        {/* Import Panel */}
        <Collapsible open={showImport} onOpenChange={setShowImport}>
          <CollapsibleContent>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Import Portfolio Data</CardTitle>
                <CardDescription>Upload an Excel file (PRVFIN tab) or IBKR Activity Statement to fetch earnings for those tickers</CardDescription>
              </CardHeader>
              <CardContent>
                <PortfolioFileImport onImportComplete={handleImport} buttonLabel="Import for Earnings Analysis" />
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Saved Sessions */}
        {savedSnapshots.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" /> Saved Analyses ({savedSnapshots.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {savedSnapshots.map(snap => (
                  <div key={snap.id} className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => restoreSnapshot(snap)} className="text-xs">
                      {format(new Date(snap.created_at), "dd MMM HH:mm")} • {snap.positions_count} pos • {snap.total_value} events
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSnapshot(snap.id)}>
                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-3 bg-orange-500/10 rounded-2xl mb-4">
            <CalendarDays className="h-10 w-10 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Earnings Calendar</h1>
          <p className="text-muted-foreground">Live earnings dates & analyst expectations from Financial Modeling Prep</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-card/80 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <Clock className="h-5 w-5 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{upcomingCount}</p>
              <p className="text-xs text-muted-foreground">Upcoming</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <CalendarDays className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{reportedCount}</p>
              <p className="text-xs text-muted-foreground">Reported</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-5 w-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">{beatsCount}</p>
              <p className="text-xs text-muted-foreground">Beats</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <TrendingDown className="h-5 w-5 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-red-600">{missesCount}</p>
              <p className="text-xs text-muted-foreground">Misses</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm col-span-2 md:col-span-1">
            <CardContent className="p-4 text-center">
              <AlertCircle className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <p className="text-sm font-bold text-foreground truncate">{nextEarnings?.ticker || '—'}</p>
              <p className="text-xs text-muted-foreground">Next Report</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="reported">Reported</SelectItem>
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={(v: any) => { setTimeRange(v); handleRefresh(); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Earnings Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Fetching live earnings data from FMP...</p>
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">No earnings data</p>
            <p className="text-muted-foreground mb-4">Import positions or load a saved analysis to view earnings calendar.</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => setShowImport(true)} className="gap-2"><Upload className="h-4 w-4" /> Import Data</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Go to Dashboard</Button>
            </div>
          </Card>
        ) : (
          <Card className="shadow-lg overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-orange-500" />
                Earnings Schedule ({filtered.length} events)
              </CardTitle>
              <CardDescription>
                {dataSource === 'import' 
                  ? `Earnings data for ${importedPositions.length} imported positions`
                  : `Live data from ${apiSource === 'nasdaq' ? 'Nasdaq' : 'Financial Modeling Prep'} API with analyst estimates`}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticker</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Days</TableHead>
                    <TableHead className="text-center">Timing</TableHead>
                    <TableHead className="text-right">EPS Est.</TableHead>
                    <TableHead className="text-right">EPS Act.</TableHead>
                    <TableHead className="text-right">Rev Est.</TableHead>
                    <TableHead className="text-right">Rev Act.</TableHead>
                    <TableHead className="text-center">Surprise</TableHead>
                    <TableHead className="text-center">Analysts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((event, idx) => (
                    <TableRow 
                      key={`${event.ticker}-${idx}`}
                      className={event.status === 'upcoming' && event.daysUntil <= 7 ? 'bg-amber-500/5' : ''}
                    >
                      <TableCell>
                        <div>
                          <span className="font-semibold text-foreground">{event.ticker}</span>
                          {event.stockName && event.stockName !== event.ticker && (
                            <p className="text-xs text-muted-foreground truncate max-w-[120px]">{event.stockName}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{event.earningsDate}</TableCell>
                      <TableCell className="text-center">{getDaysLabel(event.daysUntil)}</TableCell>
                      <TableCell className="text-center">{getTimingBadge(event.timing)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {event.epsEstimate != null ? `$${event.epsEstimate.toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {event.epsActual != null ? `$${event.epsActual.toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {event.revenueEstimate != null ? `$${event.revenueEstimate.toFixed(1)}B` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {event.revenueActual != null ? `$${event.revenueActual.toFixed(1)}B` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-center">{getSurpriseBadge(event.surprise)}</TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {event.numAnalysts || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Historical EPS Surprise Chart */}
        {hasData && (
          <div className="mt-8">
            <EarningsSurpriseChart
              tickers={[...new Set(earningsData.map(e => e.ticker))]}
            />
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-sm text-muted-foreground text-center">
          <Wifi className="h-4 w-4 inline mr-2 text-emerald-500" />
          Connected to Financial Modeling Prep API — showing real earnings dates, EPS estimates, revenue data, and analyst coverage for your portfolio holdings.
        </div>
      </div>
    </div>
  );
};

export default EarningsCalendar;
